/*
 * Levels Beyond CONFIDENTIAL
 *
 * Copyright 2003 - 2020 Levels Beyond Incorporated
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains the
 * property of Levels Beyond Incorporated and its suppliers, if
 * any. The intellectual and technical concepts contained herein
 * are proprietary to Levels Beyond Incorporated and its suppliers
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this
 * material is unlawful and strictly forbidden unless prior
 * written permission is obtained from Levels Beyond Incorporated.
 */

/**
 * Filename: create-snapshot-service.ts
 *
 * Created by kyang on 12/8/20.
 */

angular.module('prism-ui.package').service('CreateSnapshotService',
    function (
        this: JS5Object,
        _: JS5Object,
        $q: JS5Object,
        $window: JS5Object,
        NotificationService: JS5Object,
        PackageMediaResourceFactory: JS5Object,
        RequestService: JS5Object,
        WorkflowExecutionResourceFactory: JS5Object,
    ) {
        enum WorkflowStatus {
            RUNNING = 'RUNNING',
            COMPLETED = 'COMPLETED',
            STALLED = 'STALLED'
        }

        const waitForWorkflowCompletion = (getWorkflowStatusFunc: JS5Function) => {
            let deferred = $q.defer();
            let intervalId: number;
            intervalId = RequestService.createRequestInterval(() => {
                return getWorkflowStatusFunc()
                    .then((status: string) => {
                        if (status === WorkflowStatus.COMPLETED) {
                            RequestService.cancelRequestInterval(intervalId);
                            deferred.resolve('Workflow COMPLETED');
                        } else if (status === WorkflowStatus.STALLED) {
                            RequestService.cancelRequestInterval(intervalId);
                            deferred.reject('Workflow STALLED');
                        }
                    })
                    .catch((error: any) => {
                        RequestService.cancelRequestInterval(intervalId);
                        deferred.reject(error);
                    });
            }, 2000);
            return deferred.promise;
        };

        const waitForAssetCreation = (workflow: JS5Object) => {
            return waitForWorkflowCompletion(() => {
                return WorkflowExecutionResourceFactory.getWorkflowExecution({
                    id: workflow.workflowGuid
                }).$promise.then((updatedWorkflow: JS5Object) => {
                    return updatedWorkflow.status;
                });
            });
        };

        const waitForSnapshotProxy = (packageId: string) => {
            let snapshotContent: JS5Object;
            let remainingMediaRetries = 30;
            return waitForWorkflowCompletion(() => {
                return PackageMediaResourceFactory.get({
                    id: packageId
                }).$promise.then((media: JS5Object[]) => {
                    if (!snapshotContent) {
                        snapshotContent = _.find(media, (packageContent: JS5Object) => {
                            return packageContent.type === 'Image' && _.has(packageContent, 'ingestInfo.progress');
                        });
                        if (!snapshotContent) {
                            if (--remainingMediaRetries >= 0) {
                                return WorkflowStatus.RUNNING;
                            } else {
                                return $q.reject('Workflow completed, but resulting image not found');
                            }
                        }
                    }

                    let updatedContent = _.find(media, {
                        type: snapshotContent.type,
                        id: snapshotContent.id,
                    });
                    if (!_.has(updatedContent, 'ingestInfo.progress')) {
                        return $q.reject('Workflow completed, but image was replaced');
                    }
                    return _.has(updatedContent, 'proxyId') ? WorkflowStatus.COMPLETED : WorkflowStatus.RUNNING;
                });
            });
        };

        this.getSnapshotImage = (videoElement: JS5Object) => {
            let width = videoElement.videoWidth;
            let height = videoElement.videoHeight;
            let canvas = $window.document.createElement('canvas');
            let context = canvas.getContext('2d');
            canvas.width = width;
            canvas.height = height;
            context.drawImage(videoElement, 0, 0, width, height);
            return canvas.toDataURL();
        };

        this.createSnapshot = (packageId: string, timeOffset: number) => {
            NotificationService.createInfoNotification(`Creating a snapshot from video player`);
            return WorkflowExecutionResourceFactory.createPackageSnapshot({
                packageId: packageId,
                frameOffset: timeOffset,
            }).$promise
                .then(waitForAssetCreation)
                .then(() => waitForSnapshotProxy(packageId))
                .then(() => {
                    NotificationService.createSuccessNotification(`Snapshot was successfully created.`);
                })
                .catch((error: JS5Object) => {
                    NotificationService.createFailureNotification(`Failed to create the snapshot.`, error);
                    return $q.reject(error);
                });
        };
    });
